import type { ConfigLayerInternal } from './types';

/**
 * Layer-config fields that are structural, computed, or runtime state and must
 * never round-trip through a saved theme.
 *
 * Single source of truth for every stage of the theme-persistence pipeline:
 *  - S1 `useTheme.accumulateEdit`            (Esri save)
 *  - S2 `ConfigAdaptorLayer.getDifferencesFromDefaults` (Mapbox save + reload)
 *  - S3 `ThemeService.processBody`           (HTTP write gate, both paths)
 *  - S4 `ThemeService.responseParser`        (HTTP read gate, both paths)
 *
 * These four sites previously each carried their own hand-maintained list and
 * had already drifted apart (9 / 11 / 3 fields, none on the read path), which is
 * how the theme corruption behind I360G-1619 shipped: a field fixed in one stage
 * still leaked through another.
 *
 * `satisfies readonly (keyof ConfigLayerInternal)[]` is the drift guard — rename
 * or remove a field on ConfigLayerInternal and this list fails the build instead
 * of silently going stale.
 *
 * Why each group must not be persisted:
 *  - computed/@private: recomputed from icon+color+theme on every load, so a
 *    stored copy is stale by definition and overrides the fresh value.
 *  - internal tree/identity: `idPath` is regenerated, and a persisted `layers`
 *    snapshot goes stale for dynamic groups (e.g. the rehab tree).
 *  - runtime state: changes with data and in-flight requests. `requestToken` is
 *    an auth token that rides on the incoming config
 *    (see ConfigAdaptor.loadExternalConfigs) — it must never be written to the
 *    themes backend.
 *  - structural source-resolution: `tileSource`/`tileLayerName` can hold RegExp
 *    values, and `JSON.stringify(/x/) === '{}'`. A persisted `{}` is loaded back
 *    as a dead value that overrides the valid base RegExp, collapsing
 *    CompositeLayer.source to the dataServiceId fallback so the style layer is
 *    never baked into the VTL and the layer stops rendering. `_adaptor` inherits
 *    the same hazard transitively: ConfigOverrides also carries tileSource.
 *
 * `id` is deliberately absent — `processBody` discards the map key and relies on
 * `id` being present inside the value (see esri/utils/visibilityThemeEdit.ts).
 */
export const NON_PERSISTABLE_LAYER_FIELDS = [
	// computed / derived (@private)
	'iconImage',
	'patternSvg',
	// internal tree structure / identity
	'idPath',
	'layers',
	'virtualLayers',
	// runtime state
	'errored',
	'count',
	'requestToken',
	// structural source-resolution
	'dataServiceId',
	'layersEndpoint',
	'tileSource',
	'tileLayerName',
	'properties',
	'_adaptor',
	// behavioural flag with no theme-editor UI
	'hideOnNoData',
	// internal theming flags
	'validColorBy',
	'showColorBy',
	'canHeatmap',
	'colorByTheming',
] as const satisfies readonly (keyof ConfigLayerInternal)[];

/**
 * Fields stripped when *loading* a theme — the persist list minus `layers`.
 *
 * Themes saved before the strip lists existed can carry a frozen child tree, and
 * `mergeThemeEditsIntoConfig` still reads it to recover per-child overrides
 * (nested rehab-action styling). Dropping `layers` on load would discard styling
 * customers have already saved; dropping it on save is what stops new themes
 * from accumulating it in the first place.
 */
export const NON_RESTORABLE_LAYER_FIELDS = NON_PERSISTABLE_LAYER_FIELDS.filter(
	field => field !== 'layers',
);

/**
 * Returns a shallow copy of `layer` with `fields` removed. Never mutates the
 * input.
 *
 * Deliberately generic over `object` rather than typed to `ConfigLayer`: the
 * pipeline strips fields that only exist on `ConfigLayerInternal` (e.g.
 * `virtualLayers`) off values typed as `Partial<ConfigLayer>`, which a
 * destructure cannot express without a type error.
 */
export const stripLayerFields = <T extends object>(
	layer: T,
	fields: readonly string[],
): T => {
	const stripped = { ...layer } as Record<string, unknown>;
	fields.forEach(field => {
		delete stripped[field];
	});
	return stripped as T;
};
